import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import {
  ReconcileFailedJobStatusDto,
  ReconcileFailedRequestsResponseDto,
  TerminationRequestListResponseDto,
  TerminationRequestTrackingItemDto,
} from './dto/termination-request-tracking.dto'

export type EnqueueTerminationRequestParams = {
  employeePublicKey: string
  clientPublicKey: string
  terminationDate: string
  employeeName?: string
  companyEmployeeId?: string
}

/**
 * Queues a termination. Enrollment Platform executes it from its own queue, so a
 * successful response means "accepted and tracked", not "already terminated".
 */
export const enqueueTerminationRequest = async (
  params: EnqueueTerminationRequestParams
): Promise<TerminationRequestTrackingItemDto> => {
  const response = await apiClient.post<TerminationRequestTrackingItemDto>(
    '/termination-requests',
    params
  )
  return response.data
}

export type GetTerminationRequestsParams = {
  state?: string
  status?: string
  source?: string
  clientPublicKey?: string
  search?: string
  fundingStage?: string
  vcnStage?: string
  statusStage?: string
  submitDateFrom?: string
  submitDateTo?: string
  benefitEndDateFrom?: string
  benefitEndDateTo?: string
  page?: number
  limit?: number
}

export const getActiveTerminationRequest = async (
  employeePublicKey: string
): Promise<TerminationRequestTrackingItemDto | null> => {
  try {
    const response = await apiClient.get<TerminationRequestTrackingItemDto>(
      `/termination-requests/employee/${employeePublicKey}`
    )
    return response.data
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status
    if (status === 404 || status === 403) {
      return null
    }
    throw error
  }
}

export const getTerminationRequests = async (
  params: GetTerminationRequestsParams = {}
): Promise<TerminationRequestListResponseDto> => {
  const response = await apiClient.get<TerminationRequestListResponseDto>(
    '/termination-requests',
    { params }
  )
  return response.data
}

/**
 * Force-check stages on a FAILED request. Does not re-execute terminate.
 * The request is promoted to COMPLETED when every stage has landed.
 */
export const reconcileTerminationRequest = async (
  id: string
): Promise<TerminationRequestTrackingItemDto> => {
  const response = await apiClient.post<TerminationRequestTrackingItemDto>(
    `/termination-requests/${id}/reconcile`
  )
  return response.data
}

/**
 * Enqueue the singleton reconcile-failed job. A second click while one is
 * running returns the live job id.
 */
export const reconcileFailedTerminationRequests =
  async (): Promise<ReconcileFailedRequestsResponseDto> => {
    const response = await apiClient.post<ReconcileFailedRequestsResponseDto>(
      '/termination-requests/reconcile-failed'
    )
    return response.data
  }

export const getReconcileFailedJob = async (
  jobId: string
): Promise<ReconcileFailedJobStatusDto> => {
  const response = await apiClient.get<ReconcileFailedJobStatusDto>(
    `/termination-requests/reconcile-failed/${jobId}`
  )
  return response.data
}

const DEFAULT_RECONCILE_POLL_MS = 2000
const DEFAULT_RECONCILE_TIMEOUT_MS = 10 * 60 * 1000

export const waitForReconcileFailedJob = async (
  jobId: string,
  options: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<ReconcileFailedJobStatusDto> => {
  const intervalMs = options.intervalMs ?? DEFAULT_RECONCILE_POLL_MS
  const timeoutMs = options.timeoutMs ?? DEFAULT_RECONCILE_TIMEOUT_MS
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeoutMs) {
    const status = await getReconcileFailedJob(jobId)
    if (status.finished) {
      return status
    }
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs)
    })
  }

  throw new Error(
    'Reconcile is still running. Refresh the page in a few minutes.'
  )
}

export type FlexspringSourceFilePreviewDto = {
  fileName: string
  rowCount: number
  columns: string[]
  rows: Record<string, string>[]
  downloadUrl: string
  page: number
  limit: number
}

export const getFlexspringSourceFile = async (
  requestId: string,
  params: {
    page?: number
    limit?: number
    search?: string
    employeeId?: string
  } = {}
): Promise<FlexspringSourceFilePreviewDto> => {
  const response = await apiClient.get<FlexspringSourceFilePreviewDto>(
    `/termination-requests/${requestId}/flexspring-source`,
    { params }
  )
  return response.data
}
